/**
 * iter733 — Extracted from SalesPage.jsx to keep the page under
 * ~500 lines. Renders the per-branch monthly sales summary with a
 * Cards-vs-Table view toggle and a Net-vs-Gross amount toggle. All
 * data-testid attributes preserved verbatim so existing E2E tests
 * keep passing without modification.
 */
import { Card, CardContent } from '@/components/ui/card';
import { Store, Truck, TrendingUp } from 'lucide-react';
import { RiyalIcon } from '@/lib/currency';
import BranchLink from '@/components/BranchLink';

export function BranchSummaryCard({
  branchMonthlySales,
  amountView,
  branchView,
  onAmountViewChange,
  onBranchViewChange,
  branches,
}) {
  return (
    <Card className="border-0 shadow-sm bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-900/20 dark:to-blue-900/20 card-enter">
      <CardContent className="p-3.5">
        <div className="flex items-center justify-between mb-2.5 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <TrendingUp size={18} className="text-success" />
            <h3 className="font-semibold text-sm">{branchMonthlySales.month} - Branch Sales</h3>
          </div>
          <div className="flex items-center gap-3">
            <div
              className="inline-flex rounded-lg border border-border bg-background p-0.5 text-[11px]"
              data-testid="sales-branch-view-toggle"
              title="Switch between card grid and a compact table with avg ticket + count"
            >
              <button
                type="button"
                onClick={() => onBranchViewChange('cards')}
                className={`px-2.5 py-1 rounded-md font-semibold transition-colors ${branchView === 'cards' ? 'bg-foreground text-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="sales-branch-view-cards"
              >
                Cards
              </button>
              <button
                type="button"
                onClick={() => onBranchViewChange('table')}
                className={`px-2.5 py-1 rounded-md font-semibold transition-colors ${branchView === 'table' ? 'bg-foreground text-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="sales-branch-view-table"
              >
                Table
              </button>
            </div>
            <div
              className="inline-flex rounded-lg border border-border bg-background p-0.5 text-[11px]"
              data-testid="sales-amount-view-toggle"
              title="NET = post-discount (what customer paid). GROSS = pre-discount (used for VAT)."
            >
              <button
                type="button"
                onClick={() => onAmountViewChange('net')}
                className={`px-2.5 py-1 rounded-md font-semibold transition-colors ${amountView === 'net' ? 'bg-foreground text-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="sales-toggle-net"
              >
                Net
              </button>
              <button
                type="button"
                onClick={() => onAmountViewChange('gross')}
                className={`px-2.5 py-1 rounded-md font-semibold transition-colors ${amountView === 'gross' ? 'bg-foreground text-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="sales-toggle-gross"
              >
                Gross
              </button>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Total for Period <span className="opacity-60">({amountView})</span></p>
              <p className="text-xl font-bold text-success inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {branchMonthlySales.totalAll.toLocaleString()}</p>
            </div>
          </div>
        </div>

        {branchView === 'table' ? (
          <div className="overflow-x-auto rounded-lg border border-border" data-testid="sales-branch-table">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Branch</th>
                  <th className="text-right px-3 py-2 font-semibold">Sales</th>
                  <th className="text-right px-3 py-2 font-semibold">Avg Ticket</th>
                  <th className="text-right px-3 py-2 font-semibold">Cash</th>
                  <th className="text-right px-3 py-2 font-semibold">Bank</th>
                  <th className="text-right px-3 py-2 font-semibold">Credit</th>
                  <th className="text-right px-3 py-2 font-semibold">Online</th>
                  <th className="text-right px-3 py-2 font-semibold">Total</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(branchMonthlySales.byBranch)
                  .sort(([, a], [, b]) => (b.total || 0) - (a.total || 0))
                  .map(([branchName, data]) => {
                    const branchId = data.branch_id || branches.find(b => b.name === branchName)?.id || null;
                    const count = data.count || 0;
                    const avg = count > 0 ? data.total / count : 0;
                    return (
                      <tr
                        key={branchName}
                        className="border-t hover:bg-muted/30 transition-colors"
                        data-testid={`sales-branch-row-${branchId || branchName}`}
                      >
                        <td className="px-3 py-2 font-medium">
                          <div className="flex items-center gap-1.5">
                            <Store size={12} className="text-info shrink-0" />
                            {branchId ? <BranchLink branchId={branchId} name={branchName} /> : <span>{branchName}</span>}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{count.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{avg.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-success">{data.cash > 0 ? data.cash.toLocaleString() : '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-info">{data.bank > 0 ? data.bank.toLocaleString() : '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-warning">{data.credit > 0 ? data.credit.toLocaleString() : '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-purple-600">{data.online > 0 ? data.online.toLocaleString() : '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-bold text-foreground">{data.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    );
                  })}
              </tbody>
              <tfoot className="bg-muted/40 border-t font-semibold">
                <tr>
                  <td className="px-3 py-2">Total</td>
                  <td colSpan={6}></td>
                  <td className="px-3 py-2 text-right tabular-nums">{branchMonthlySales.totalAll.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {Object.entries(branchMonthlySales.byBranch).map(([branchName, data]) => {
              const branchId = data.branch_id || branches.find(b => b.name === branchName)?.id || null;
              return (
                <div key={branchName} className="bg-white dark:bg-muted rounded-xl p-3 border">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Store size={14} className="text-info" />
                    <span className="text-xs font-medium truncate">
                      {branchId ? <BranchLink branchId={branchId} name={branchName} /> : branchName}
                    </span>
                  </div>
                  <p className="text-lg font-bold text-success inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {data.total.toLocaleString()}</p>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {data.cash > 0 && <span className="text-[10px] px-1.5 py-0.5 bg-success/20 text-success rounded">Cash: {data.cash.toLocaleString()}</span>}
                    {data.bank > 0 && <span className="text-[10px] px-1.5 py-0.5 bg-info/20 text-info rounded">Bank: {data.bank.toLocaleString()}</span>}
                    {data.credit > 0 && <span className="text-[10px] px-1.5 py-0.5 bg-warning/20 text-warning rounded">Credit: {data.credit.toLocaleString()}</span>}
                    {data.online > 0 && <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">Online: {data.online.toLocaleString()}</span>}
                  </div>
                </div>
              );
            })}

            {branchMonthlySales.totalOnline > 0 && (
              <div className="bg-purple-50 dark:bg-purple-900/30 rounded-xl p-3 border border-purple-200">
                <div className="flex items-center gap-1.5 mb-2">
                  <Truck size={14} className="text-purple-500" />
                  <span className="text-xs font-medium">Online Total</span>
                </div>
                <p className="text-lg font-bold text-purple-600 inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {branchMonthlySales.totalOnline.toLocaleString()}</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
